/**
 * Backend emits ISO strings from naive `datetime.utcnow()` (no trailing Z
 * or offset). Without that marker, `new Date()` treats the string as
 * LOCAL time — which on a +08:00 client makes a fresh install render as
 * "Connected 8h ago". Normalize by appending Z when no offset is present.
 *
 * Mirror of admin-front/src/lib/format.ts::parseServerIso (shipped in
 * admin commit a2b6bdf). Keep this implementation identical.
 */

/** Rendered in place of a timestamp we cannot parse. Never '' — an empty
 *  string collapses the layout of the fixed-width cells these feed. */
export const NO_DATE = '—';

type DateLocales = string | string[] | undefined;

/**
 * Total over its input: returns null rather than an Invalid Date.
 *
 * Some conversations legitimately carry no timestamp — a zero-message chat
 * created by the on-demand `request_human` path has never had a message
 * appended, so the API omits `last_message_at` entirely. The old signature
 * took a bare `string`, so `undefined` reached the regex as the literal
 * "undefined", failed the offset test, and produced `new Date("undefinedZ")`
 * → Invalid Date → "Invalid Date" in the Inbox. Worse, callers doing
 * `.getTime()` got NaN, and every NaN comparison is false, so date filters
 * silently passed those rows through.
 *
 * Returning null forces every call site to decide what a missing timestamp
 * means, and the compiler enforces that they do.
 */
export function parseServerIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasOffset = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Epoch ms, or null when the timestamp is missing/unparseable. */
export function serverIsoToMillis(iso: string | null | undefined): number | null {
  return parseServerIso(iso)?.getTime() ?? null;
}

/** `date.toLocaleDateString(locales, options)`, or NO_DATE. */
export function formatServerDate(
  iso: string | null | undefined,
  locales?: DateLocales,
  options?: Intl.DateTimeFormatOptions,
): string {
  return parseServerIso(iso)?.toLocaleDateString(locales, options) ?? NO_DATE;
}

/** `date.toLocaleTimeString(locales, options)`, or NO_DATE. */
export function formatServerTime(
  iso: string | null | undefined,
  locales?: DateLocales,
  options?: Intl.DateTimeFormatOptions,
): string {
  return parseServerIso(iso)?.toLocaleTimeString(locales, options) ?? NO_DATE;
}

/** `date.toLocaleString(locales, options)`, or NO_DATE. */
export function formatServerDateTime(
  iso: string | null | undefined,
  locales?: DateLocales,
  options?: Intl.DateTimeFormatOptions,
): string {
  return parseServerIso(iso)?.toLocaleString(locales, options) ?? NO_DATE;
}
